import React from 'react';
import { Receipt, TrendingUp, Wallet, ArrowLeft } from 'lucide-react';
import Button from '../../components/UI/Button';

export type ReportCategory = 'donation' | 'income' | 'expense';

interface ReportCategorySelectorProps {
  onSelect: (category: ReportCategory) => void;
}

const CATEGORIES: {
  id: ReportCategory;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'donation',
    label: 'Donation',
    description: 'Region, office, fund type, cashier, donor, and transaction reports',
    icon: <Receipt className="h-6 w-6" />,
  },
  {
    id: 'income',
    label: 'Income',
    description: 'Department, type, matrix, operator, and transaction reports',
    icon: <TrendingUp className="h-6 w-6" />,
  },
  {
    id: 'expense',
    label: 'Expense',
    description: 'Type, account, operator, and transaction reports',
    icon: <Wallet className="h-6 w-6" />,
  },
];

const CategoryCard: React.FC<{
  active?: boolean;
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}> = ({ icon, label, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start gap-4 p-5 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-left w-full"
    aria-label={`Open ${label} reports`}
  >
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
      {icon}
    </div>
    <div className="min-w-0">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{label}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
    </div>
  </button>
);

const ReportCategorySelector: React.FC<ReportCategorySelectorProps> = ({ onSelect }) => (
  <div className="space-y-6 pb-10">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Choose a category to generate financial reports
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {CATEGORIES.map((cat) => (
        <CategoryCard
          key={cat.id}
          icon={cat.icon}
          label={cat.label}
          description={cat.description}
          onClick={() => onSelect(cat.id)}
        />
      ))}
    </div>
  </div>
);

export default ReportCategorySelector;

export const ReportCategoryBackButton: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Button type="button" variant="secondary" size="sm" onClick={onBack} className="gap-2">
    <ArrowLeft className="h-4 w-4" />
    All categories
  </Button>
);
